import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Button, Stack, CheckboxField } from '@curves/ui-base';
import { useHistory, useLocation } from 'react-router-dom';
import { useMainLayoutContext } from '@Local/Components/Layout/Context/MainLaydoutContext';
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from '@Constants/dataEnums';
import { MEMBER_CYCLE_PURCHASE_ROUTER, MEMBER_CYCLE_PURCHASE_PRODUCT_SELECT_ROUTER, MEMBER_CYCLE_PURCHASE_SUCCESS_ROUTER } from '@Router/routerPath';
import { CyclePurchaseStepper } from '@Local/CyclePurchase/Components';
import useMemberCyclePurchaseApi from '@Apis/useMemberCyclePurchaseApi';

/**
 * 循環訂單確認變更頁
 * 顯示「變更前」vs「變更後」商品對比與合計金額，供會員最終確認。
 * 確認後呼叫 API 送出變更，成功導向成功頁；可返回重新選品。
 * 入口：CyclePurchaseProductSelectPage → handleConfirm（location.state: { order, cartSummary }）
 */

/** ISO datetime → "YYYY/MM/DD" */
const toDateStr = (iso) => (iso ? iso.slice(0, 10).replace(/-/g, '/') : '');

const CyclePurchaseConfirmPage = () => {
    const { setHeaderType, setFooterType, setThemeType, setHeaderContent } = useMainLayoutContext();
    const history = useHistory();
    const location = useLocation();
    const { order } = location.state || {};

    // 功能：變更後的選品明細。
    // 以 useMemo 固定識別：若直接在解構時給預設值 []，未帶 state 時每次 render 都會產生新陣列，
    // 使依賴它的 useCallback／useMemo 每次都失效。
    const cartSummary = useMemo(() => location.state?.cartSummary ?? [], [location.state]);
    const { postMemberCyclePurchaseChangeFlavorApi } = useMemberCyclePurchaseApi();

    // 功能：同意扣款重算條款的 CheckboxField ref（必填，僅前端把關，不送後端）
    const agreementRef = useRef(null);

    // 功能：返回選品頁，將當前選品狀態恢復為 sourceCart 以便繼續編輯
    // 以 useCallback 固定識別，避免每次 render 產生新函式而讓下方設定版面的 useEffect 反覆執行
    const handleBack = useCallback(() => {
        const editCart = Object.fromEntries(cartSummary.map(({ productID, quantity }) => [productID, quantity]));
        history.replace(MEMBER_CYCLE_PURCHASE_PRODUCT_SELECT_ROUTER, { order, sourceCart: editCart });
    }, [history, order, cartSummary]);

    // 功能：守衛（未傳訂單時轉回列表）＋設定版面
    useEffect(() => {
        setHeaderType(HEADER_TYPE.DEFAULT);
        setFooterType(FOOTER_TYPE.HIDE);
        setThemeType(THEME_TYPE.DEFAULT);
        setHeaderContent(<CyclePurchaseStepper activeStep={1} onBack={handleBack} />);

        if (!order) history.replace(MEMBER_CYCLE_PURCHASE_ROUTER);

        return () => setHeaderContent(null);
    }, [history, order, setHeaderType, setFooterType, setThemeType, setHeaderContent, handleBack]);

    // 功能：計算變更後商品合計金額
    const totalPrice = useMemo(() => cartSummary.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0), [cartSummary]);

    if (!order) return null;

    // 功能：送出變更 API，成功後帶入生效時機資料前往成功頁
    const handleConfirm = async () => {
        // 未勾選同意條款則不送出（isError() 同時觸發必填錯誤提示）
        if (agreementRef.current?.isError()) return;

        const body = {
            cycleOrderID: order.cycleOrderID,
            items: cartSummary.map(({ productID, variantID, quantity }) => ({ productID, variantID, quantity })),
        };

        const data = await postMemberCyclePurchaseChangeFlavorApi(body);
        if (!data) return;

        history.replace(MEMBER_CYCLE_PURCHASE_SUCCESS_ROUTER, {
            order,
            cartSummary,
            effectType: data.effectTimingText.trim(), // "immediate" | "next"
            effectDate: toDateStr(data.effectDate),
            nextShipDate: toDateStr(data.nextShipDate),
        });
    };

    return (
        <Box className="cycle-purchase-confirm-page">
            <Box className="container">
                <Typography className="cp-confirm-title">確認變更</Typography>
                <Typography className="cp-confirm-subtitle">請確認以下訂單變更內容</Typography>

                {/* 變更前 */}
                <Typography className="cp-confirm-section-label cp-confirm-section-label-before">變更前</Typography>
                <Box className="cp-confirm-before-card">
                    {order.items?.map((item) => (
                        <Box key={item.productID} className="cp-confirm-item-row">
                            <Typography className="cp-confirm-item-name">{item.productName}</Typography>
                            <Typography className="cp-confirm-item-qty">x{item.quantity}</Typography>
                        </Box>
                    ))}
                    <Box className="cp-confirm-total-row cp-confirm-total-row-before">
                        <Typography className="cp-confirm-total-label">合計</Typography>
                        <Typography className="cp-confirm-total-price">${Number(order.saleAmount ?? 0).toLocaleString()}</Typography>
                    </Box>
                </Box>

                {/* 變更為橫帶 */}
                <Box className="cp-confirm-change-band">
                    <Box className="cp-confirm-change-band-label">↓ 變更為</Box>
                </Box>

                {/* 變更後（含合計） */}
                <Box className="cp-confirm-after-card">
                    {cartSummary.map((item) => (
                        <Box key={item.productID} className="cp-confirm-item-row">
                            <Typography className="cp-confirm-item-name cp-confirm-item-name-after">{item.productName}</Typography>
                            <Typography className="cp-confirm-item-qty">x{item.quantity}</Typography>
                        </Box>
                    ))}
                    <Box className="cp-confirm-total-row">
                        <Typography className="cp-confirm-total-label">合計</Typography>
                        <Typography className="cp-confirm-total-price">${totalPrice.toLocaleString()}</Typography>
                    </Box>
                </Box>

                {/* 同意條款（必填） */}
                <Box className="cp-confirm-agreement">
                    <CheckboxField ref={agreementRef} required helperText="請先勾選同意條款後再送出">
                        <CheckboxField.Checkbox
                            value={true}
                            color="primary"
                            label={
                                <Typography className="cp-confirm-agreement-text">
                                    我同意變更後，刷卡扣款金額，將依以上新選購品項之單價重新計算。
                                </Typography>
                            }
                        />
                    </CheckboxField>
                </Box>

                {/* 操作按鈕 */}
                <Stack direction="column" spacing={1.5} className="cp-confirm-actions">
                    <Button variant="contained" fullWidth onClick={handleConfirm}>
                        確認變更
                    </Button>
                    <Button variant="text" fullWidth onClick={handleBack}>
                        返回修改
                    </Button>
                </Stack>
            </Box>
        </Box>
    );
};

export default CyclePurchaseConfirmPage;
